import { useMemo } from 'react'
import { PagesPDFWrapper } from '../../../../features/ui'
import { CodedDataType } from '../../../../tables'
import { Roadmap } from '../../../logistics'
import { TruckPreparationPDFPage } from './truckPreparationPDFPage'
import { TruckPreparationPDFBlock, TruckPreparationPDFData } from './types'

// Space of a page measured in table rows: a block title plus the table header take BLOCK_HEADER_ROWS rows and the
// first page also has the roadmap info
const FIRST_PAGE_ROWS = 23
const PAGE_ROWS = 30
const BLOCK_HEADER_ROWS = 3
const MIN_BLOCK_ROWS = 2
// The last page keeps room for the signatures
const SIGNATURES_ROWS = 4

type Props = {
    id: string
    roadmap: Roadmap
    data: TruckPreparationPDFData
    previewMode?: boolean
}

export const TruckPreparationPDF = (props: Props) => {
    const { id, roadmap, data, previewMode } = props

    // Each shipment is split between pages when it doesn't fit in the current one
    const pagesBlocks = useMemo(() => {
        const sections = data.shipments
            .map(shipment => ({ title: shipment.title, subtitle: shipment.customer, rows: shipment.rows }))
            .filter(section => section.rows.length > 0)

        const pages: TruckPreparationPDFBlock[][] = [[]]
        let usedRows = 0
        const capacity = () => pages.length === 1 ? FIRST_PAGE_ROWS : PAGE_ROWS
        const newPage = () => {
            pages.push([])
            usedRows = 0
        }

        sections.forEach(section => {
            let rows = section.rows
            let continued = false
            while (rows.length > 0) {
                const availableRows = capacity() - usedRows - BLOCK_HEADER_ROWS
                const fitsAll = availableRows >= rows.length
                if (!fitsAll && availableRows < MIN_BLOCK_ROWS && usedRows > 0) {
                    newPage()
                    continue
                }
                const pageRows = rows.slice(0, Math.max(availableRows, MIN_BLOCK_ROWS))
                pages[pages.length - 1].push({ title: section.title, subtitle: section.subtitle, rows: pageRows, continued })
                usedRows += BLOCK_HEADER_ROWS + pageRows.length
                rows = rows.slice(pageRows.length)
                continued = true
                if (rows.length > 0) {
                    newPage()
                }
            }
        })

        if (capacity() - usedRows < SIGNATURES_ROWS) {
            newPage()
        }

        return pages
    }, [data])

    const renderPages = pagesBlocks.map((blocks, pageIndex) =>
        () => (
            <TruckPreparationPDFPage
                pageIndex={pageIndex}
                pagesAmount={pagesBlocks.length}
                data={data}
                blocks={blocks}
            />
        )
    )

    return (
        <PagesPDFWrapper
            id={id}
            dataType={roadmap as unknown as CodedDataType}
            previewMode={previewMode}
            renderPages={renderPages}
        />
    )
}
